import { Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';  

const LOGO_URL = "/logo.png";

const footerLinks = {
  navigation: [
    { name: 'Home', href: '/' },
    { name: 'Leistungen', href: '/leistungen' },
    { name: 'Immobilien', href: '/immobilien' },
    { name: 'Referenzen', href: '/referenzen' },
    { name: 'Über uns', href: '/ueber-uns' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Kontakt', href: '/kontakt' },
  ],
  services: [
    { name: 'Wertermittlung', href: '/leistungen/wertermittlung' },
    { name: 'Verkauf', href: '/leistungen/verkauf' },
    { name: 'Vermietung', href: '/leistungen/vermietung' },
    { name: 'Vermittlung', href: '/leistungen/vermittlung' },
  ],
  legal: [
    { name: 'Impressum', href: '/impressum' },
    { name: 'Datenschutz', href: '/datenschutz' },
    { name: 'AGB', href: '/agb' },
  ],
};

export default function Footer() {
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0a1120] border-t border-[#d1af6a]/20" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Dogan Immobilien" className="h-16 w-auto" />
            </Link>
            <p className="text-[#c8c6c4] text-sm leading-relaxed mb-6 max-w-sm">
              Ihr vertrauensvoller Partner für Immobilien im Rhein-Main-Gebiet. 
              Professionelle Wertermittlung, diskrete Verkaufsabwicklung und persönliche Beratung.
            </p>
            <div className="space-y-3">
              <a 
                href="tel:+491756900374" 
                className="flex items-center gap-3 text-[#c8c6c4] hover:text-[#d1af6a] transition-colors text-sm"
              >
                <Phone className="w-4 h-4 text-[#d1af6a]" />
                +49 175 6900374
              </a>
              <a 
                href="mailto:info@immobilien-dogan.com" 
                className="flex items-center gap-3 text-[#c8c6c4] hover:text-[#d1af6a] transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-[#d1af6a]" />
                info@immobilien-dogan.com
              </a>
              <div className="flex items-center gap-3 text-[#c8c6c4] text-sm">
                <MapPin className="w-4 h-4 text-[#d1af6a]" />
                Rhein-Main-Gebiet
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-accent text-[#d1af6a] text-xs tracking-[0.2em] mb-6">
              NAVIGATION
            </h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-[#c8c6c4] hover:text-[#d1af6a] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-accent text-[#d1af6a] text-xs tracking-[0.2em] mb-6">
              LEISTUNGEN
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-[#c8c6c4] hover:text-[#d1af6a] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-accent text-[#d1af6a] text-xs tracking-[0.2em] mb-6">
              RECHTLICHES
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-[#c8c6c4] hover:text-[#d1af6a] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              {/* Instagram */}
              <a 
                href="https://instagram.com/doganimmobilien" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#d1af6a] hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>

              {/* LinkedIn */}
              <a 
                href="https://www.linkedin.com/in/serdar-dogan-23485022b/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#d1af6a] hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>

              {/* TikTok */}
              <a 
                href="https://www.tiktok.com/@dogan.immobilien3" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#d1af6a] hover:text-white transition-colors"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#d1af6a]/20 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#c8c6c4]/60 text-sm text-center">
            © {new Date().getFullYear()} Dogan Immobilien – Serdar Dogan | Alle Rechte vorbehalten
          </p>
          <a
            href="https://eb-agency.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity"
            title="Website erstellt von EB Agency"
          >
            <span className="text-[#c8c6c4] text-xs">Website erstellt von</span>
            <img
              src="/eb-agency-logo.png"
              alt="EB Agency"
              className="h-6 w-auto"
            />
          </a>
        </div>
      </div>
    </footer>
  );
}
